import { useEffect, useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { DollarSign, MessageCircle, Pencil, Trash2, Loader2, ExternalLink, Ban as BanIcon, RotateCcw, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { getBillingPixInfo, listCharges, updateCharge, setChargeActive, settleChargeManually } from "@/lib/api/charges.functions";
import { PAYMENT_METHODS } from "@/lib/payment-methods";
import { deletePixProof } from "@/lib/api/pix-proofs.functions";
import { sendWhatsappSequence } from "@/lib/api/whatsapp.functions";
import { paymentMethodLabel, paymentMethodChannel } from "@/lib/payment-methods";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { brToday } from "@/lib/tz";
import { buildPaymentWhatsappMessages, createMercadoPagoPixLink } from "@/lib/pix/whatsapp-payment";

export const Route = createFileRoute("/_authenticated/m/cobranca")({
  component: CobrancaPage,
});

type Charge = {
  source: "contract" | "sale" | "rental" | "fee";
  kind: "contract" | "uniform" | "product" | "rental" | "fee";
  id: string;
  amount: number;
  status: string;
  note: string | null;
  created_at: string;
  due_date?: string | null;
  student_id: string | null;
  student_name: string;
  parent_name: string | null;
  phone: string | null;
  plan_name: string | null;
  payment_method: string | null;
};

const fmtBRL = (n: number) =>
  new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(n || 0);

function CobrancaPage() {
  const loadFn = useServerFn(listCharges);
  const updateFn = useServerFn(updateCharge);
  const deleteFn = useServerFn(deletePixProof);
  const pixInfoFn = useServerFn(getBillingPixInfo);
  const sendSeqFn = useServerFn(sendWhatsappSequence);

  const setActiveFn = useServerFn(setChargeActive);
  const settleFn = useServerFn(settleChargeManually);

  const [settling, setSettling] = useState<Charge | null>(null);
  const [settleMethod, setSettleMethod] = useState("dinheiro");
  const [settleBusy, setSettleBusy] = useState(false);
  const [settleDiscount, setSettleDiscount] = useState("");
  const [settlePaid, setSettlePaid] = useState("");

  const settleDue = Number(settling?.amount ?? 0);
  const settleEffective = Math.max(0, settleDue - (Number(settleDiscount.replace(",", ".")) || 0));
  const settlePaidNum = settlePaid.trim() === ""
    ? settleEffective
    : Math.min(Number(settlePaid.replace(",", ".")) || 0, settleEffective);
  const settleIsPartial = settlePaidNum > 0 && settlePaidNum < settleEffective;
  const settleRemaining = Math.max(0, settleEffective - settlePaidNum);

  const openSettle = (c: Charge) => {
    setSettling(c);
    setSettleDiscount("");
    setSettlePaid("");
  };

  const doSettle = async (exempt: boolean) => {
    if (!settling) return;
    setSettleBusy(true);
    try {
      const res: any = await settleFn({
        data: {
          id: settling.id,
          payment_method: settleMethod,
          exempt,
          discount: exempt ? 0 : (Number(settleDiscount.replace(",", ".")) || 0),
          paid_amount: exempt ? null : settlePaidNum,
        },
      });
      toast.success(
        exempt
          ? "Cobrança isentada"
          : res?.partial
            ? `Pagamento parcial registrado — saldo de ${fmtBRL(res.remaining ?? settleRemaining)}`
            : "Cobrança quitada e lançada no caixa",
      );
      setSettling(null);
      reload();
    } catch (e: any) {
      toast.error(e?.message || "Erro ao quitar cobrança");
    } finally {
      setSettleBusy(false);
    }
  };


  const [view, setView] = useState<"pendentes" | "inativas">("pendentes");
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState<Charge[]>([]);
  const [editing, setEditing] = useState<Charge | null>(null);
  const [editAmount, setEditAmount] = useState("");
  const [editNote, setEditNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<Charge | null>(null);
  const [billing, setBilling] = useState<Charge | null>(null);
  const [billingMessages, setBillingMessages] = useState<string[]>([]);
  const [billingLoading, setBillingLoading] = useState(false);
  const [billingSending, setBillingSending] = useState(false);
  const [billingPhone, setBillingPhone] = useState("");

  const reload = (v: "pendentes" | "inativas" = view) => {
    setLoading(true);
    loadFn({ data: { view: v } })
      .then((r: any) => setItems(r.charges ?? []))
      .catch((e: any) => toast.error(e?.message || "Erro ao carregar"))
      .finally(() => setLoading(false));
  };

  useEffect(() => { reload(view); }, [view]);

  const toggleActive = async (c: Charge, active: boolean) => {
    try {
      await setActiveFn({ data: { id: c.id, source: c.source, kind: c.kind, active } });
      toast.success(active ? "Cobrança reativada" : "Cobrança inativada");
      reload();
    } catch (e: any) {
      toast.error(e?.message || "Erro ao atualizar cobrança");
    }
  };


  // ---- Filtros ----
  const [fStudent, setFStudent] = useState("");
  const [fParent, setFParent] = useState("");
  const [fPhone, setFPhone] = useState("");
  const [fDue, setFDue] = useState<"all" | "vencida" | "a_vencer">("all");
  const [fFrom, setFFrom] = useState("");
  const [fTo, setFTo] = useState("");

  const norm = (v: string) =>
    (v || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();

  const today = brToday();
  const isOverdue = (c: Charge) => !!c.due_date && c.due_date < today;

  const filtered = useMemo(() => items.filter((c) => {
    if (fStudent && !norm(c.student_name).includes(norm(fStudent))) return false;
    if (fParent && !norm(c.parent_name ?? "").includes(norm(fParent))) return false;
    if (fPhone && !(c.phone ?? "").replace(/\D/g, "").includes(fPhone.replace(/\D/g, ""))) return false;
    if (fDue !== "all") {
      const over = isOverdue(c);
      if (fDue === "vencida" ? !over : over) return false;
    }
    if (fFrom && (!c.due_date || c.due_date < fFrom)) return false;
    if (fTo && (!c.due_date || c.due_date > fTo)) return false;
    return true;
  }), [items, fStudent, fParent, fPhone, fDue, fFrom, fTo, today]);

  const total = useMemo(() => filtered.reduce((s, i) => s + i.amount, 0), [filtered]);

  const openEdit = (c: Charge) => {
    setEditing(c);
    setEditAmount(String(c.amount));
    setEditNote(c.note ?? "");
  };

  const handleSaveEdit = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      await updateFn({ data: { id: editing.id, amount: Number(editAmount), note: editNote || null } });
      toast.success("Cobrança atualizada");
      setEditing(null);
      reload();
    } catch (e: any) {
      toast.error(e?.message || "Erro ao salvar");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    try {
      await deleteFn({ data: { id: confirmDelete.id } });
      toast.success("Cobrança excluída");
      setConfirmDelete(null);
      reload();
    } catch (e: any) {
      toast.error(e?.message || "Erro ao excluir");
    }
  };

  const chargeLabel = (c: Charge) => c.note || c.plan_name || (c.kind === "uniform" ? "Uniforme" : c.kind === "product" ? "Mercadoria" : c.kind === "rental" ? "Reserva de espaço" : c.kind === "fee" ? "Taxa" : "Plano");

  const openBilling = async (c: Charge) => {
    setBilling(c);
    setBillingPhone(c.phone || "");
    setBillingMessages([]);
    setBillingLoading(true);
    try {
      const tc: any = await pixInfoFn();
      const payer = c.parent_name || c.student_name || "responsável";
      const amount = Number(c.amount ?? 0);
      const valorFmt = fmtBRL(amount);
      const item = chargeLabel(c);
      const subject = c.kind === "rental"
        ? `Cliente: *${c.student_name}*`
        : c.kind === "contract"
        ? `Aluno(a): *${c.student_name}*`
        : c.student_id
          ? `Aluno(a)/comprador: *${c.student_name}*`
          : `Comprador: *${c.student_name}*`;

      const channel = paymentMethodChannel(c.payment_method || "pix_comprovante");
      const introBase = `Olá ${payer}! Aqui é do ${tc.name || "centro de treinamento"}.\n\nVocê tem uma cobrança em aberto:\n\n📌 *${item}*\n${subject}\n💰 Valor: *${valorFmt}*`;

      let mercadoPago: { initPoint?: string | null; brcode?: string | null } | undefined;
      let intro = introBase;
      if (channel === "mercadopago") {
        intro = `${introBase}\n\nA próxima mensagem traz o link de pagamento via Mercado Pago.`;
        try {
          const res = await createMercadoPagoPixLink({
            training_center_id: tc.id,
            transaction_amount: amount,
            description: item,
            payer_email: null,
            reference_id: `charge:${c.source}:${c.kind}:${c.id}`,
            method: c.payment_method,
          });
          mercadoPago = { initPoint: res.init_point, brcode: res.brcode };
        } catch (e: any) {
          toast.error(`Mercado Pago: ${e?.message || "erro"}`);
        }
      } else if (channel === "presencial") {
        intro = `${introBase}\n\n📍 Pagamento *presencial* — o acerto pode ser feito na secretaria do centro de treinamento.\n\nQualquer dúvida, é só responder por aqui.`;
      } else {
        intro = `${introBase}\n\nA próxima mensagem traz o *Pix copia-e-cola* já com o valor incluso. Toque e segure para copiar e cole no app do banco.\n\n⚠️ Após realizar o pagamento, envie o comprovante neste mesmo número.`;
      }


      const messages = buildPaymentWhatsappMessages({
        channel,
        method: c.payment_method,
        introMessage: intro,
        amount,
        pixKey: tc.pix_key,
        pixBeneficiary: tc.pix_beneficiary || tc.name,
        city: tc.city,
        txid: c.id.replace(/-/g, "").slice(0, 25),
        description: item,
        mercadoPago,
      });
      setBillingMessages(messages);
    } catch (e: any) {
      toast.error(e?.message || "Erro ao preparar cobrança");
      setBilling(null);
    } finally {
      setBillingLoading(false);
    }
  };

  const updateBillingMsg = (i: number, value: string) => {
    setBillingMessages((arr) => arr.map((m, idx) => (idx === i ? value : m)));
  };

  const sendBillingApi = async () => {
    const phone = (billingPhone || billing?.phone || "").trim();
    if (!phone) { toast.error("Informe o WhatsApp de destino."); return; }
    const toSend = billingMessages.map((m) => m.trim()).filter(Boolean);
    if (!toSend.length) { toast.error("Nenhuma mensagem para enviar."); return; }
    setBillingSending(true);
    try {
      const r: any = await sendSeqFn({ data: {
        recipient: { name: billing?.parent_name || billing?.student_name || "cliente", phone },
        messages: toSend,
      } });
      if (r.sent > 0) {
        toast.success(`${r.sent} mensagem(ns) enviada(s) via WhatsApp`);
        setBilling(null);
      } else {
        toast.error(r.errors?.[0] || "Falha ao enviar");
      }
    } catch (e: any) {
      toast.error(e?.message || "Erro ao enviar cobrança");
    } finally {
      setBillingSending(false);
    }
  };

  const openBillingWeb = () => {
    const raw = (billingPhone || billing?.phone || "").replace(/\D/g, "");
    if (!raw) { toast.error("Informe o WhatsApp de destino."); return; }
    const num = raw.length <= 11 ? `55${raw}` : raw;
    const joined = billingMessages.join("\n\n────────\n\n");
    window.open(`https://wa.me/${num}?text=${encodeURIComponent(joined)}`, "_blank");
  };


  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <DollarSign className="h-7 w-7 text-primary" />
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Cobrança</h1>
            <p className="text-sm text-muted-foreground">
              Cobranças em aberto: mensalidades Pix (comprovante, presencial ou online) e vendas.
            </p>
          </div>
        </div>
        <div className="text-right">
          <div className="text-xs text-muted-foreground">Total em aberto</div>
          <div className="text-2xl font-bold">{fmtBRL(total)}</div>
        </div>
      </div>

      <div className="inline-flex rounded-lg border p-1 bg-muted/40">
        {(["pendentes", "inativas"] as const).map((v) => (
          <button
            key={v}
            type="button"
            onClick={() => setView(v)}
            className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
              view === v ? "bg-background shadow font-medium" : "text-muted-foreground"
            }`}
          >
            {v === "pendentes" ? "Pendentes" : "Inativas"}
          </button>
        ))}
      </div>

      <Card>
        <CardContent className="p-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          <div className="grid gap-1">
            <label className="text-xs text-muted-foreground">Aluno / cliente</label>
            <Input value={fStudent} onChange={(e) => setFStudent(e.target.value)} placeholder="Nome do aluno" />
          </div>
          <div className="grid gap-1">
            <label className="text-xs text-muted-foreground">Responsável</label>
            <Input value={fParent} onChange={(e) => setFParent(e.target.value)} placeholder="Nome do responsável" />
          </div>
          <div className="grid gap-1">
            <label className="text-xs text-muted-foreground">Telefone</label>
            <Input value={fPhone} onChange={(e) => setFPhone(e.target.value)} placeholder="DDD + número" />
          </div>
          <div className="grid gap-1">
            <label className="text-xs text-muted-foreground">Situação do vencimento</label>
            <Select value={fDue} onValueChange={(v) => setFDue(v as any)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas</SelectItem>
                <SelectItem value="vencida">Vencidas</SelectItem>
                <SelectItem value="a_vencer">A vencer</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="grid gap-1">
            <label className="text-xs text-muted-foreground">Vencimento de</label>
            <Input type="date" value={fFrom} onChange={(e) => setFFrom(e.target.value)} />
          </div>
          <div className="grid gap-1">
            <label className="text-xs text-muted-foreground">Vencimento até</label>
            <Input type="date" value={fTo} onChange={(e) => setFTo(e.target.value)} />
          </div>
          <div className="sm:col-span-2 lg:col-span-3 flex justify-between items-center">
            <span className="text-xs text-muted-foreground">{filtered.length} cobrança(s)</span>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => { setFStudent(""); setFParent(""); setFPhone(""); setFDue("all"); setFFrom(""); setFTo(""); }}
            >
              Limpar filtros
            </Button>
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin" /></div>
      ) : filtered.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            {view === "inativas"
              ? "Nenhuma cobrança inativada."
              : "Nenhuma cobrança pendente no momento."}
          </CardContent>
        </Card>
      ) : (

        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {filtered.map((c) => (
            <Card key={c.id}>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center justify-between gap-2">
                  <span className="truncate">{c.student_name}</span>
                  {c.kind === "fee" && <Badge className="bg-amber-500 hover:bg-amber-500 text-white">Taxa</Badge>}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                {c.parent_name && (
                  <div><span className="text-muted-foreground">Responsável:</span> {c.parent_name}</div>
                )}
                {c.phone && (
                  <div><span className="text-muted-foreground">WhatsApp:</span> {c.phone}</div>
                )}
                {c.plan_name && (
                  <div><span className="text-muted-foreground">Plano:</span> {c.plan_name}</div>
                )}
                {c.payment_method && (
                  <div className="text-xs text-muted-foreground">
                    {paymentMethodLabel(c.payment_method)}
                  </div>
                )}
                <div className="flex items-center gap-2 pt-1 flex-wrap">
                  <div className="text-xl font-bold">{fmtBRL(c.amount)}</div>
                  <Badge variant={view === "inativas" ? "outline" : "secondary"}>
                    {view === "inativas" ? "Inativa" : "Pendente"}
                  </Badge>
                  {c.due_date && (
                    <Badge
                      className={isOverdue(c)
                        ? "bg-destructive text-destructive-foreground hover:bg-destructive"
                        : "bg-emerald-600 text-white hover:bg-emerald-600"}
                    >
                      {isOverdue(c) ? "Vencida" : "A vencer"} · {new Date(`${c.due_date}T00:00:00`).toLocaleDateString("pt-BR")}
                    </Badge>
                  )}
                </div>
                <div className="flex flex-wrap gap-2 pt-3">
                  {view === "pendentes" ? (
                    <>
                      <Button size="sm" onClick={() => openBilling(c)}>
                        <MessageCircle className="h-4 w-4 mr-1" /> Cobrar
                      </Button>
                      {(c.source === "contract" || c.source === "fee" || c.source === "rental") && (
                        <Button
                          size="sm"
                          className="bg-emerald-600 hover:bg-emerald-700 text-white"
                          onClick={() => { openSettle(c); setSettleMethod("dinheiro"); }}
                          title="Quitar manualmente (lança no caixa)"
                        >
                          <CheckCircle2 className="h-4 w-4 mr-1" /> Quitar
                        </Button>
                      )}
                      <Button size="sm" variant="outline" onClick={() => openEdit(c)}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => toggleActive(c, false)}
                        title="Inativar cobrança (mantém o débito no histórico)"
                      >
                        <BanIcon className="h-4 w-4" />
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => setConfirmDelete(c)}>
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button size="sm" variant="outline" onClick={() => toggleActive(c, true)}>
                        <RotateCcw className="h-4 w-4 mr-1" /> Reativar
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => setConfirmDelete(c)}>
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </>
                  )}
                </div>

              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={!!settling} onOpenChange={(o) => !o && setSettling(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Quitar cobrança manualmente</DialogTitle></DialogHeader>
          {settling && (
            <div className="space-y-3 text-sm">
              <div className="rounded-lg bg-muted/40 p-3">
                <div><strong>{settling.student_name}</strong> — {chargeLabel(settling)}</div>
                <div className="text-lg font-bold">{fmtBRL(settling.amount)}</div>
              </div>
              <div className="space-y-1">
                <span className="text-xs text-muted-foreground">Forma de pagamento</span>
                <Select value={settleMethod} onValueChange={setSettleMethod}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PAYMENT_METHODS.map((m) => (
                      <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <span className="text-xs text-muted-foreground">Desconto (R$)</span>
                  <Input
                    type="number" min="0" step="0.01" inputMode="decimal"
                    value={settleDiscount}
                    onChange={(e) => setSettleDiscount(e.target.value)}
                    placeholder="0,00"
                  />
                </div>
                <div className="space-y-1">
                  <span className="text-xs text-muted-foreground">Valor recebido agora (R$)</span>
                  <Input
                    type="number" min="0" step="0.01" inputMode="decimal"
                    value={settlePaid}
                    onChange={(e) => setSettlePaid(e.target.value)}
                    placeholder={settleEffective.toFixed(2)}
                  />
                </div>
              </div>
              <div className="rounded-md bg-muted/40 p-2 text-xs space-y-0.5">
                <div>Valor devido com desconto: <b>{fmtBRL(settleEffective)}</b></div>
                {settleIsPartial && (
                  <div className="text-amber-600">
                    Pagamento parcial — saldo restante de <b>{fmtBRL(settleRemaining)}</b> continuará em aberto.
                  </div>
                )}
              </div>
              <p className="rounded-md border border-amber-500/40 bg-amber-500/10 p-2 text-xs">
                ⚠ A quitação manual desta cobrança será lançada como <b>entrada no caixa</b>, identificando o colaborador logado.
              </p>
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setSettling(null)} disabled={settleBusy}>Cancelar</Button>
            <Button variant="secondary" onClick={() => doSettle(true)} disabled={settleBusy}>Isentar (100%)</Button>
            <Button className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => doSettle(false)} disabled={settleBusy}>
              {settleBusy ? "Processando..." : settleIsPartial ? "Registrar pagamento parcial" : "Confirmar quitação"}
            </Button>
          </DialogFooter>

        </DialogContent>
      </Dialog>

      <Dialog open={!!billing} onOpenChange={(o) => !o && setBilling(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageCircle className="h-5 w-5" /> Enviar cobrança via WhatsApp
            </DialogTitle>
          </DialogHeader>
          {billingLoading ? (
            <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin" /></div>
          ) : billing ? (
            <div className="space-y-3">
              <div className="text-sm bg-muted/40 p-3 rounded-lg">
                <div><strong>{billing.student_name}</strong> — {chargeLabel(billing)} — {fmtBRL(billing.amount)}</div>
                <div className="text-xs text-muted-foreground mt-1">
                  Para: {billing.parent_name || billing.student_name || "—"}
                </div>
              </div>
              <div className="space-y-1">
                <label className="text-xs font-medium text-muted-foreground">WhatsApp de destino (DDD + número)</label>
                <input
                  type="tel"
                  value={billingPhone}
                  onChange={(e) => setBillingPhone(e.target.value)}
                  placeholder="Ex: 31999998888"
                  className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                />
              </div>
              <div className="space-y-2">
                <label className="text-xs font-medium text-muted-foreground">
                  Mensagens ({billingMessages.length}) — serão enviadas em sequência
                </label>
                {billingMessages.map((m, i) => (
                  <div key={i} className="rounded-md border bg-background">
                    <div className="text-[10px] uppercase tracking-wide text-muted-foreground px-2 pt-1">
                      Mensagem {i + 1}
                    </div>
                    <Textarea
                      value={m}
                      onChange={(e) => updateBillingMsg(i, e.target.value)}
                      className="min-h-[70px] border-0 focus-visible:ring-0"
                    />
                  </div>
                ))}
              </div>
            </div>
          ) : null}
          <DialogFooter>
            <Button variant="outline" onClick={() => setBilling(null)} disabled={billingSending}>Fechar</Button>
            <Button variant="outline" onClick={openBillingWeb} disabled={billingSending || billingLoading}>
              <ExternalLink className="h-4 w-4 mr-2" /> Abrir WhatsApp Web
            </Button>
            <Button onClick={sendBillingApi} disabled={billingSending || billingLoading}>
              {billingSending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <MessageCircle className="h-4 w-4 mr-2" />}
              Enviar pela API
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Editar cobrança</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-medium text-muted-foreground">Valor (R$)</label>
              <Input
                type="number" step="0.01" min="0"
                value={editAmount} onChange={(e) => setEditAmount(e.target.value)}
              />
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground">Observação</label>
              <Textarea value={editNote} onChange={(e) => setEditNote(e.target.value)} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancelar</Button>
            <Button onClick={handleSaveEdit} disabled={saving}>
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Salvar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir cobrança?</AlertDialogTitle>
            <AlertDialogDescription>
              A cobrança de <strong>{confirmDelete?.student_name}</strong> no valor de{" "}
              <strong>{fmtBRL(confirmDelete?.amount ?? 0)}</strong> será removida.
              Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete}>Excluir</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
